"use client"

import { Link, useT } from "@/lib/router"
import { SITE } from "@/lib/site"
import { Button, CheckList, FAQList, Icon, PageHeader, Section, SectionHeading, type AnyIcon } from "@/components/ui"

const capabilities: { icon: AnyIcon; title: { ar: string; en: string }; body: { ar: string; en: string } }[] = [
  {
    icon: "code",
    title: { ar: "المواقع والمتاجر والتطبيقات", en: "Websites, stores and applications" },
    body: { ar: "نطوّر تجارب رقمية لعملائك، من مواقع الشركات والمتاجر إلى تطبيقات الجوال والأنظمة المخصصة.", en: "We build digital experiences for your clients, from company websites and online stores to mobile apps and custom software." },
  },
  {
    icon: "erp",
    title: { ar: "أنظمة إدارة الأعمال والعملاء", en: "Business systems and CRM" },
    body: { ar: "نهيّئ أنظمة تربط المبيعات والعملاء والعمليات، ونربطها بأدوات عمل العميل حسب احتياجاته.", en: "We configure systems that connect sales, customers and operations, with integrations shaped around each client's needs." },
  },
  {
    icon: "workflow",
    title: { ar: "الأتمتة والمساعدات الذكية", en: "Automation and AI assistants" },
    body: { ar: "نبسّط المهام المتكررة ونصمّم مسارات عمل ومساعدات ذكية تخدم الفريق، مع مراجعة الخطوات المهمة.", en: "We simplify repeated tasks and build workflows and AI assistants that support the team, with review for important steps." },
  },
  {
    icon: "cloud",
    title: { ar: "الاستضافة والصيانة والدعم", en: "Hosting, maintenance and support" },
    body: { ar: "يمكن أن يشمل التعاون تشغيل الحلول ومتابعتها وصيانتها بعد الإطلاق، وفق نطاق الدعم المتفق عليه.", en: "The partnership can include hosting, monitoring and maintenance after launch, within an agreed support scope." },
  },
]

const steps = [
  {
    title: { ar: "نفهم نموذج التعاون", en: "Understand the partnership" },
    body: { ar: "نتعرّف على مؤسستك وعملائك والخدمات التي ترغب في تقديمها، سواء لمشروع محدد أو لتعاون مستمر.", en: "We learn about your organization, clients and the services you want to offer, for a specific project or an ongoing collaboration." },
  },
  {
    title: { ar: "نتفق على النطاق والأدوار", en: "Agree on scope and roles" },
    body: { ar: "نحدّد المخرجات والتكلفة والجدول وآلية التواصل، ونوضّح مسؤوليات كل طرف وطريقة التسليم باسمك.", en: "We define deliverables, cost, schedule and communication, with clear responsibilities and a delivery approach under your brand." },
  },
  {
    title: { ar: "ننفّذ بالتنسيق مع فريقك", en: "Deliver with your team" },
    body: { ar: "نتابع التنفيذ والمراجعات مع فريقك، ثم نسلّم العمل بالصيغة المتفق عليها وننظّم الدعم عند الحاجة.", en: "We coordinate implementation and reviews with your team, deliver in the agreed format and arrange support where needed." },
  },
]

const faqs = [
  {
    q: { ar: "ماذا يعني تقديم الخدمات بنموذج White Label؟", en: "What does white label delivery mean?" },
    a: { ar: "يعني أن تتولى أوركست التنفيذ التقني، بينما تقدّم أنت الخدمة لعميلك تحت علامتك التجارية. نتفق مسبقًا على طريقة عرض العمل والتواصل والتسليم بما يناسب نموذج تعاوننا.", en: "Orkest handles technical delivery while you offer the service to your client under your own brand. We agree in advance on how work is presented, communicated and handed over." },
  },
  {
    q: { ar: "هل يمكن أن نبدأ بمشروع واحد؟", en: "Can we start with a single project?" },
    a: { ar: "نعم، يمكن أن يبدأ التعاون بمشروع محدد، أو يشمل تنفيذ خدمات متعددة بصورة مستمرة. نحدّد النموذج الأنسب بعد فهم احتياجات فريقك.", en: "Yes. We can collaborate on a specific project or deliver multiple services on an ongoing basis. We agree on the best approach after understanding your team's needs." },
  },
  {
    q: { ar: "كيف يتم التواصل مع العميل النهائي؟", en: "How is communication with the end client handled?" },
    a: { ar: "يمكن أن يمر التواصل بالكامل عبر فريقك. وإذا احتاج العمل إلى مشاركة مباشرة منا، نتفق معك أولًا على دورنا وطريقة تقديم الفريق للعميل.", en: "Communication can run entirely through your team. If our direct involvement is useful, we first agree with you on our role and how the team is introduced to the client." },
  },
  {
    q: { ar: "كيف نحدّد شروط التعاون والتسعير؟", en: "How are partnership terms and pricing set?" },
    a: { ar: "نناقش نطاق العمل والمواعيد وطريقة التسليم والدعم، ثم نعدّ عرضًا يناسب المشروع. نتفق أيضًا على السرية وحقوق استخدام وتسليم المخرجات قبل بدء التنفيذ.", en: "We discuss scope, timing, delivery and support, then prepare a project-specific proposal. Confidentiality and rights to use and receive deliverables are also agreed before implementation begins." },
  },
]

export default function Partners() {
  const t = useT()
  const partnershipEmail = `mailto:${SITE.email}?subject=${encodeURIComponent(t({ ar: "طلب تعاون بنموذج White Label", en: "White label partnership enquiry" }))}`

  return (
    <>
      <PageHeader
        crumbs={[{ label: t({ ar: "الشركاء", en: "Partners" }) }]}
        eyebrow={t({ ar: "شراكات العلامة البيضاء", en: "White label partnerships" })}
        title={t({ ar: "خبرتنا التقنية، تحت علامتك التجارية.", en: "Our expertise. Your brand." })}
        lead={t({ ar: "نتعاون مع المؤسسات والشركات والوكالات لتقديم خدماتنا الرقمية باسمها. أنت تقود علاقة العميل، ونحن نساند فريقك في بناء الحلول وتنفيذها.", en: "We work with organizations, companies and agencies to deliver our digital services under their brand. You lead the client relationship; we support your team with technical delivery." })}
      >
        <Button to={partnershipEmail}>{t({ ar: "ناقش معنا الشراكة", en: "Discuss a partnership" })}</Button>
        <Button to="/services" variant="secondary">{t({ ar: "استكشف خدماتنا", en: "Explore our services" })}</Button>
      </PageHeader>

      <Section>
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow={t({ ar: "نموذج التعاون", en: "How we work together" })}
              title={t({ ar: "امتداد تقني لفريقك.", en: "An extension of your team." })}
              lead={t({ ar: "العلامة البيضاء (White Label) تتيح لك تقديم حلول ينفّذها فريق أوركست تحت اسم مؤسستك، لتوسيع خدماتك والاستعانة بخبرات تقنية حسب متطلبات المشروع.", en: "White label delivery lets you offer solutions built by Orkest under your organization's name, expanding your services with technical expertise matched to the project." })}
            />
            <p className="mt-6 text-base leading-7 text-slate">
              {t({ ar: "يناسب هذا النموذج وكالات التسويق والتصميم، وشركات الاستشارات والخدمات، والمؤسسات التي تبحث عن شريك لتنفيذ الحلول الرقمية لعملائها.", en: "This model suits marketing and design agencies, consultancies, service companies and organizations seeking a digital delivery partner for their clients." })}
            </p>
          </div>
          <div className="grid gap-5 lg:col-span-7 sm:grid-cols-2">
            <article className="rounded-r16 bg-mist p-7">
              <Icon name="user" className="size-8 text-blue" />
              <h3 className="mt-5 text-xl font-medium text-ink">{t({ ar: "أنت تقود العلاقة", en: "You lead the relationship" })}</h3>
              <div className="mt-5">
                <CheckList columns={1} items={[
                  t({ ar: "علامتك التجارية أمام العميل.", en: "Your brand in front of the client." }),
                  t({ ar: "إدارة العلاقة وفهم احتياجات العميل.", en: "Client relationship and requirements." }),
                  t({ ar: "العرض التجاري وتنسيق المراجعات.", en: "Commercial proposal and review coordination." }),
                ]} />
              </div>
            </article>
            <article className="rounded-r16 bg-mist p-7">
              <Icon name="code" className="size-8 text-blue" />
              <h3 className="mt-5 text-xl font-medium text-ink">{t({ ar: "نحن ندعم التنفيذ", en: "We support delivery" })}</h3>
              <div className="mt-5">
                <CheckList columns={1} items={[
                  t({ ar: "تخطيط الحل والتنفيذ التقني.", en: "Solution planning and technical delivery." }),
                  t({ ar: "الاختبار والتسليم بالتنسيق مع فريقك.", en: "Testing and handover with your team." }),
                  t({ ar: "الصيانة والدعم حسب الاتفاق.", en: "Maintenance and support as agreed." }),
                ]} />
              </div>
            </article>
          </div>
        </div>
      </Section>

      <Section tone="mist">
        <SectionHeading
          eyebrow={t({ ar: "خدمات باسمك", en: "Services under your brand" })}
          title={t({ ar: "حلول يمكنك تقديمها لعملائك.", en: "More solutions for your clients." })}
          lead={t({ ar: "اختر خدمة محددة أو اجمع عدة خدمات حسب المشروع. نحدّد معك نطاق التنفيذ المناسب لكل عميل.", en: "Choose a specific service or combine several around a project. We agree with you on the delivery scope for each client." })}
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {capabilities.map((item) => (
            <article key={item.title.en} className="rounded-r16 bg-white p-8">
              <span className="inline-flex size-12 items-center justify-center rounded-r8 bg-ice text-blue"><Icon name={item.icon} /></span>
              <h3 className="mt-6 text-xl font-medium text-ink">{t(item.title)}</h3>
              <p className="mt-3 text-base leading-7 text-slate">{t(item.body)}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow={t({ ar: "خطوات الشراكة", en: "Getting started" })} title={t({ ar: "تعاون واضح، من البداية إلى التسليم.", en: "A clear process, from introduction to handover." })} />
        <ol className="mt-10 grid gap-8 md:grid-cols-3">
          {steps.map((step, index) => (
            <li key={step.title.en} className="border-t border-line pt-6">
              <span className="text-sm font-medium text-blue" aria-hidden="true">0{index + 1}</span>
              <h3 className="mt-4 text-xl font-medium text-ink">{t(step.title)}</h3>
              <p className="mt-3 text-base leading-7 text-slate">{t(step.body)}</p>
            </li>
          ))}
        </ol>
        <div className="mt-20 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHeading title={t({ ar: "قبل أن نبدأ", en: "Before we begin" })} /></div>
          <div className="lg:col-span-8"><FAQList items={faqs.map((item) => ({ q: t(item.q), a: t(item.a) }))} /></div>
        </div>
      </Section>

      <Section tone="blue">
        <div className="max-w-3xl">
          <SectionHeading light title={t({ ar: "لنقدّم قيمة أكبر لعملائك، معًا.", en: "Let's deliver more for your clients, together." })} lead={t({ ar: "عرّفنا بمؤسستك والخدمات التي ترغب في تقديمها، وسنناقش معك كيف يمكن لأوركست دعم فريقك تحت علامتك التجارية.", en: "Tell us about your organization and the services you want to offer. We'll discuss how Orkest can support your team under your brand." })} />
          <div className="mt-8 flex flex-wrap gap-3">
            <Button to={partnershipEmail} variant="inverse">{t({ ar: "تواصل بشأن الشراكة", en: "Enquire about a partnership" })}</Button>
            <Link to="/contact" className="inline-flex min-h-12 items-center rounded-r8 px-5 font-medium text-white ring-1 ring-white/60 hover:bg-white/10">{t({ ar: "استخدم نموذج التواصل", en: "Use our contact form" })}</Link>
          </div>
          <a href={partnershipEmail} className="mt-6 inline-flex min-h-11 items-center gap-3 text-base text-white underline underline-offset-4"><Icon name="mail" className="size-5" /><bdi dir="ltr">{SITE.email}</bdi></a>
        </div>
      </Section>
    </>
  )
}
